import { type FC, useCallback, useState } from "react";

import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { IconButton, Modal, TextArea } from "@snapchat/snap-design-system";
import { RefreshIcon } from "@snapchat/snap-design-system-icons";

import { type ConfigMap, type ConfigTypes, cofConfigIds, overridesStore } from "../../../cof-feature-gates/models";
import { type CofConfigId } from "../../../gql-generated/types";
import { Flexbox } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";

import { CofConfigsTrigger } from "./CofConfigsContainer";

const Form = styled.form`
    width: 800px;
`;

const marginBottom = css`
    margin-bottom: 10px;
`;

export const CofConfigsContainer: FC = () => {
    const [{ visible, onCancel }, showModal] = useToggleState();

    const [configsMap, setConfigsMap] = useState<ConfigMap>(overridesStore?.getItem<ConfigMap>() ?? {});

    const updateOverride = useCallback(
        (cofConfigId: CofConfigId, valueStr: string) => {
            try {
                const valueParsed = JSON.parse(valueStr) as ConfigTypes;
                setConfigsMap({
                    ...configsMap,
                    [cofConfigId]: valueParsed,
                });
            } catch (error) {
                console.log((error as Error).message);
            }
        },
        [configsMap],
    );

    const resetOverrides = useCallback(() => {
        overridesStore?.removeItem();
    }, []);

    const applyChanges = useCallback(() => {
        overridesStore?.setItem(configsMap);
    }, [configsMap]);

    if (!overridesStore?.getItem<ConfigMap>()) {
        return <></>;
    }

    return (
        <>
            <CofConfigsTrigger onClick={showModal} />
            <Modal
                visible={visible}
                title={
                    <Flexbox alignItems="center" justifyContent="space-between">
                        <div>Feature Gate Overrides</div>
                        <IconButton icon={<RefreshIcon />} onClick={resetOverrides} />
                    </Flexbox>
                }
                onAction={() => {
                    applyChanges();
                    onCancel();
                }}
                actionText="Apply"
                onCancel={onCancel}
                cancelText="Cancel"
                width="auto"
            >
                <Form>
                    {cofConfigIds.map((cofConfigId) => (
                        <TextArea
                            className={marginBottom}
                            key={cofConfigId}
                            autoSize={true}
                            label={cofConfigId}
                            defaultValue={JSON.stringify(configsMap[cofConfigId])}
                            onChange={(value: string) => {
                                updateOverride(cofConfigId, value);
                            }}
                        ></TextArea>
                    ))}
                </Form>
            </Modal>
        </>
    );
};
